
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoadingBuffer from "../components/LoadingBuffer";
import { handleUniversalAddToCart, getProductStock } from "../utils/cartHelper";
import ProductCartButton from "../components/ProductCartButton";

const BestSellers = () => {
  const navigate = useNavigate();


  // ============================================================
  // STATE
  // ============================================================

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingProduct, setAddingProduct] = useState(null);

  // ============================================================
  // FETCH BEST SELLERS
  // ============================================================

  useEffect(() => {
    fetchBestSellers();

    const handleSync = () => fetchBestSellers();
    window.addEventListener("focus", handleSync);
    window.addEventListener("storage", handleSync);
    window.addEventListener("vanya_category_sync", handleSync);
    return () => {
      window.removeEventListener("focus", handleSync);
      window.removeEventListener("storage", handleSync);
      window.removeEventListener("vanya_category_sync", handleSync);
    };
  }, []);

  const fetchBestSellers = async () => {
    try {
      setLoading(true);

      const [res, catRes] = await Promise.all([
        fetch("https://api.vanyacollectionsrajahmundry.com/products/all"),
        fetch("https://api.vanyacollectionsrajahmundry.com/categories").catch(() => null),
      ]);

      if (!res.ok) {
        throw new Error(
          "Failed to fetch products"
        );
      }

      const data = await res.json();
      const catData = catRes && catRes.ok ? await catRes.json() : [];
      const apiCategories = Array.isArray(catData) ? catData : [];
      const normalizeCat = (v = "") =>
        String(v || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

      const bestSellers = Array.isArray(data)
        ? data
            .filter(
              (p) =>
                !p.is_hold &&
                String(
                  p.type || ""
                )
                  .trim()
                  .toLowerCase() ===
                "best seller"
            )
            .map((p) => {
              const normCat = normalizeCat(p.category);
              const matchedCat = apiCategories.find(
                (c) => normalizeCat(c?.category) === normCat
              );
              return matchedCat?.category
                ? { ...p, category: matchedCat.category }
                : p;
            })
        : [];

      setProducts(bestSellers);
    } catch (err) {
      console.error(
        "Error fetching best sellers:",
        err
      );

      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // QUICK VIEW
  // ============================================================

  const handleQuickView = (
    e,
    product
  ) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(
      `/product/${product.id}`
    );
  };

  // ============================================================
  // ADD TO CART
  // ============================================================

  const handleAddToCart = async (
    e,
    product
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent duplicate clicks
    if (
      addingProduct ===
      product.id
    ) {
      return;
    }

    try {
      setAddingProduct(product.id);

      const availableStock = getProductStock(product);
      if (availableStock <= 0) {
        alert(`"${product.name || 'This saree'}" is currently out of stock.`);
        return;
      }

      const result = await handleUniversalAddToCart(product);

      if (result.outOfStock) {
        alert(result.message || `"${product.name || 'This saree'}" is currently out of stock.`);
        return;
      }

      if (result.needsColourSelection) {
        alert("Please select a colour first.");
        navigate(`/product/${product.id}`);
        return;
      }

      if (result.success) {
        alert(result.message || "Product added to cart successfully!");
      }
    } catch (error) {
      console.error(
        "ADD TO CART ERROR:",
        error
      );

      alert(
        error?.message ||
          "Unable to add product to cart"
      );
    } finally {
      setAddingProduct(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      style={styles.container}
      className="bestsellers-container"
    >
      {/* ====================================================== */}
      {/* BACK BUTTON */}
      {/* ====================================================== */}

      <button
        type="button"
        onClick={() =>
          navigate(-1)
        }
        style={
          styles.backButton
        }
        className="back-button"
        aria-label="Go back"
      >
        <span className="back-arrow">
          ←
        </span>

        <span>
          Back
        </span>
      </button>

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        style={styles.header}
        className="bestsellers-header"
      >
        <div
          style={
            styles.headerContent
          }
        >
          <span
            style={
              styles.tagline
            }
            className="bestsellers-tagline"
          >
            ✦ FAN FAVOURITES ✦
          </span>

          <h2
            style={
              styles.title
            }
            className="bestsellers-title"
          >
            Best Sellers
          </h2>

          <div
            style={
              styles.underline
            }
          ></div>
        </div>
      </div>

      {/* ====================================================== */}
      {/* LOADING */}
      {/* ====================================================== */}

      {loading && (
        <LoadingBuffer 
          message="Curating India's Most Loved Best Sellers..." 
          fullPage={true} 
        />
      )}

      {/* ====================================================== */}
      {/* EMPTY */}
      {/* ====================================================== */}

      {!loading &&
        products.length ===
          0 && (
          <div
            style={
              styles.message
            }
          >
            No best sellers available at the moment.
          </div>
        )}

      {/* ====================================================== */}
      {/* PRODUCT GRID */}
      {/* ====================================================== */}

      {!loading &&
        products.length >
          0 && (
          <div
            className="bestsellers-grid"
            style={
              styles.grid
            }
          >
            {products.map(
              (product) => {
                const isOutOfStock = getProductStock(product) <= 0;
                return (
                <div
                  key={
                    product.id
                  }
                  style={
                    styles.productCard
                  }
                  className="product-card"
                >
                  {/* ========================================== */}
                  {/* IMAGE + ACTIONS */}
                  {/* ========================================== */}

                  <Link
                    to={`/product/${product.id}`}
                    style={{
                      textDecoration:
                        "none",
                      color:
                        "inherit",
                      display:
                        "block",
                    }}
                  >
                    <div
                      style={
                        styles.imageWrapper
                      }
                      className="product-image-wrapper"
                    >
                      {/* ====================================== */}
                      {/* IMAGE */}
                      {/* ====================================== */}

                      <img
                        src={
                          product.img_url ||
                          product.mainImage ||
                          product.thumbnails?.[0] ||
                          product.variants?.[0]?.mainImage ||
                          product.variants?.[0]?.thumbnails?.[0] ||
                          ""
                        }
                        alt={
                          product.name ||
                          "Product"
                        }
                        style={
                          styles.image
                        }
                        className="product-image"
                        loading="lazy"
                      />

                      {/* ====================================== */}
                      {/* BADGES */}
                      {/* ====================================== */}

                      <div
                        style={
                          styles.badgeContainer
                        }
                        className="badge-container"
                      >
                        {isOutOfStock ? (
                          <span
                            style={{
                              backgroundColor: "#C22730",
                              color: "#FFFFFF",
                              fontSize: "8.5px",
                              padding: "4px 9px",
                              borderRadius: "999px",
                              fontWeight: "700",
                              letterSpacing: "1px",
                              boxShadow: "0 2px 6px rgba(194, 39, 48, 0.4)",
                            }}
                          >
                            OUT OF STOCK
                          </span>
                        ) : (
                          <span
                            style={
                              styles.badgeBestseller
                            }
                          >
                            BESTSELLER
                          </span>
                        )}

                        {Number(
                          product.discount
                        ) > 0 && (
                          <span
                            style={
                              styles.badgeDiscount
                            }
                          >
                            {
                              product.discount
                            }
                            % OFF
                          </span>
                        )}
                      </div>

                      {/* ====================================== */}
                      {/* DESKTOP ACTIONS */}
                      {/* ====================================== */}

                      <div
                        className="actions desktop-actions"
                        style={
                          styles.desktopActionOverlay
                        }
                      >
                        {/* QUICK VIEW */}

                        <button
                          type="button"
                          style={
                            styles.quickView
                          }
                          onClick={(
                            e
                          ) =>
                            handleQuickView(
                              e,
                              product
                            )
                          }
                        >
                          Quick View
                        </button>

                        {/* ADD TO BAG */}
                        <ProductCartButton product={product} />
                      </div>
                    </div>
                  </Link>

                  {/* ========================================== */}
                  {/* PRODUCT INFO */}
                  {/* ========================================== */}

                  <Link
                    to={`/product/${product.id}`}
                    style={{
                      textDecoration:
                        "none",
                      color:
                        "inherit",
                      display:
                        "block",
                      flexGrow: 1,
                    }}
                  >
                    <div
                      style={
                        styles.info
                      }
                      className="product-info"
                    >
                      <p
                        style={
                          styles.category
                        }
                        className="product-category"
                      >
                        {product.category ||
                          "Handloom Saree"}
                      </p>

                      <h3
                        style={
                          styles.productName
                        }
                        className="product-name"
                        title={
                          product.name
                        }
                      >
                        {
                          product.name
                        }
                      </h3>

                      <div
                        style={
                          styles.priceRow
                        }
                      >
                        <span
                          style={
                            styles.currentPrice
                          }
                          className="current-price"
                        >
                          ₹
                          {Number(
                            product.price ||
                              0
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </span>

                        {product.old_price &&
                          Number(
                            product.old_price
                          ) >
                            Number(
                              product.price
                            ) && (
                            <span
                              style={
                                styles.oldPrice
                              }
                              className="old-price"
                            >
                              ₹
                              {Number(
                                product.old_price
                              ).toLocaleString(
                                "en-IN"
                              )}
                            </span>
                          )}
                      </div>
                    </div>
                  </Link>

                  {/* ========================================== */}
                  {/* MOBILE ACTIONS */}
                  {/* ========================================== */}

                  <div
                    className="actions mobile-actions"
                    style={
                      styles.mobileActionRow
                    }
                  >
                    {/* MOBILE QUICK VIEW */}

                    <button
                      type="button"
                      style={
                        styles.quickView
                      }
                      onClick={(
                        e
                      ) =>
                        handleQuickView(
                          e,
                          product
                        )
                      }
                    >
                      Quick View
                    </button>

                    {/* MOBILE ADD TO BAG */}
                    <ProductCartButton product={product} compact={true} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* ====================================================== */}
      {/* RESPONSIVE CSS */}
      {/* ====================================================== */}

      <style>
        {`
          .bestsellers-container,
          .bestsellers-container * {
            box-sizing: border-box;
          }

          .bestsellers-container {
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
          }

          /* ================================================= */
          /* BACK BUTTON */
          /* ================================================= */

          .back-button {
            transition:
              color 0.2s ease,
              transform 0.2s ease;
          }

          .back-button:hover {
            color: #C22730 !important;
            transform: translateX(-4px);
          }

          .back-button:active {
            transform:
              translateX(-2px)
              scale(0.98);
          }

          .back-button:focus {
            outline:
              2px solid
              rgba(212, 175, 55, 0.6);

            outline-offset: 3px;
          }

          .back-arrow {
            font-size: 21px;
            line-height: 1;
          }

          /* ================================================= */
          /* PRODUCT CARD */
          /* ================================================= */

          .product-card {
            height: 100%;
            display: flex;
            flex-direction: column;

            transition:
              transform 0.35s
                cubic-bezier(
                  0.165,
                  0.84,
                  0.44,
                  1
                ),
              box-shadow 0.35s ease,
              border-color 0.35s ease;

            position: relative;
            background: #FFFFFF;
            border-radius: 14px;
            overflow: hidden;
            border:
              1px solid
              rgba(
                75,
                41,
                84,
                0.08
              );
            box-shadow:
              0 8px 24px
              rgba(
                75,
                41,
                84,
                0.06
              );
          }

          /* ================================================= */
          /* MOBILE ACTIONS HIDDEN DESKTOP */
          /* ================================================= */

          .mobile-actions {
            display: none !important;
          }

          /* ================================================= */
          /* DESKTOP HOVER */
          /* ================================================= */

          @media (hover: hover) {

            .product-card:hover {
              transform:
                translateY(-5px);

              border-color:
                #D4AF37 !important;

              box-shadow:
                0 16px 36px
                rgba(
                  75,
                  41,
                  84,
                  0.12
                ),
                0 0 15px
                rgba(
                  212,
                  175,
                  55,
                  0.2
                ) !important;
            }

            .product-card:hover
            .product-image {
              transform:
                scale(1.04);
            }

            .desktop-actions {
              opacity: 0;

              visibility:
                hidden;

              pointer-events:
                none;

              transition:
                opacity 0.25s ease,
                visibility 0.25s ease;
            }

            .product-card:hover
            .desktop-actions {
              opacity: 1;

              visibility:
                visible;

              pointer-events:
                auto;
            }
          }

          /* ================================================= */
          /* PRODUCT IMAGE */
          /* ================================================= */

          .product-image {
            transition:
              transform 0.5s
              cubic-bezier(
                0.165,
                0.84,
                0.44,
                1
              );
          }

          /* ================================================= */
          /* TABLET */
          /* ================================================= */

          @media
          (min-width: 769px)
          and
          (max-width: 1100px) {

            .bestsellers-container {
              padding:
                30px 18px 50px !important;
            }

            .bestsellers-grid {
              grid-template-columns:
                repeat(
                  3,
                  minmax(0, 1fr)
                ) !important;

              gap: 18px !important;
            }

            .product-image-wrapper {
              height:
                280px !important;
            }
          }

          /* ================================================= */
          /* MOBILE */
          /* ================================================= */

          @media (max-width: 768px) {

            .bestsellers-container {
              padding:
                20px 10px 35px !important;
            }

            .back-button {
              min-height:
                40px !important;

              min-width:
                85px !important;

              padding:
                8px 14px !important;

              margin-bottom:
                18px !important;

              font-size:
                13px !important;

              -webkit-tap-highlight-color:
                transparent;

              touch-action:
                manipulation;
            }

            .back-arrow {
              font-size: 20px !important;
            }

            .bestsellers-header {
              margin-bottom:
                22px !important;

              padding-bottom:
                12px !important;
            }

            .bestsellers-tagline {
              font-size:
                9px !important;

              letter-spacing:
                2.5px !important;
            }

            .bestsellers-title {
              font-size:
                1.7rem !important;
            }

            .bestsellers-grid {
              grid-template-columns:
                repeat(
                  2,
                  minmax(0, 1fr)
                ) !important;

              gap:
                10px !important;

              padding:
                0 !important;

              width:
                100% !important;
            }

            .product-image-wrapper {
              height:
                185px !important;
            }

            .product-info {
              padding:
                10px 8px 4px 8px !important;
            }

            .product-category {
              font-size:
                8.5px !important;

              letter-spacing:
                1px !important;
            }

            .product-name {
              font-size:
                12.5px !important;

              margin-bottom:
                6px !important;
            }

            .current-price {
              font-size:
                13px !important;
            }

            .old-price {
              font-size:
                10px !important;
            }

            /* Hide desktop actions */

            .desktop-actions {
              display:
                none !important;
            }

            /* Show mobile actions */

            .mobile-actions {
              display:
                flex !important;

              opacity:
                1 !important;

              visibility:
                visible !important;

              pointer-events:
                auto !important;

              position:
                relative !important;

              background:
                transparent !important;

              backdrop-filter:
                none !important;

              padding:
                6px 8px 10px 8px !important;

              gap:
                6px !important;
            }

            .mobile-actions button {
              min-height:
                34px !important;

              font-size:
                10px !important;

              padding:
                6px 4px !important;

              border-radius:
                8px !important;

              letter-spacing:
                0.3px !important;

              -webkit-tap-highlight-color:
                transparent;

              touch-action:
                manipulation;

              transition: transform 0.15s ease, filter 0.15s ease;
            }

            .mobile-actions button:active {
              transform: scale(0.96) !important;
            }

            .mobile-actions button:disabled {
              opacity:
                0.6;

              cursor:
                not-allowed;
            }

            .badge-container {
              top:
                8px !important;

              left:
                8px !important;
            }
          }

          /* ================================================= */
          /* SMALL ANDROID */
          /* ================================================= */

          @media (max-width: 400px) {

            .bestsellers-container {
              padding-left:
                8px !important;

              padding-right:
                8px !important;
            }

            .bestsellers-grid {
              gap:
                8px !important;
            }

            .product-image-wrapper {
              height:
                165px !important;
            }

            .product-info {
              padding:
                8px 6px 2px 6px !important;
            }

            .product-name {
              font-size:
                11.5px !important;
            }

            .product-category {
              font-size:
                7.5px !important;
            }

            .current-price {
              font-size:
                12px !important;
            }

            .old-price {
              font-size:
                9px !important;
            }

            .badge-container {
              transform:
                scale(0.92);

              transform-origin:
                top left;
            }
          }
        `}
      </style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    width: "100%",
    maxWidth: "1300px",
    padding: "30px 20px 50px",
    margin: "0 auto",
    fontFamily:
      '"Montserrat", sans-serif',
    backgroundColor: "#FAF5FC",
    boxSizing: "border-box",
    overflowX: "hidden",
  },

  backButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    background: "#FFFFFF",
    border: "1px solid rgba(75, 41, 84, 0.15)",
    borderRadius: "999px",
    color: "#4B2954",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    padding: "8px 18px",
    marginBottom: "25px",
    fontFamily:
      '"Montserrat", sans-serif',
    letterSpacing: "0.5px",
    boxShadow: "0 2px 8px rgba(75, 41, 84, 0.04)",
    WebkitTapHighlightColor:
      "transparent",
    touchAction:
      "manipulation",
  },

  header: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems:
      "flex-end",
    marginBottom: "35px",
    borderBottom:
      "1px solid rgba(212, 175, 55, 0.25)",
    paddingBottom: "15px",
    width: "100%",
  },

  headerContent: {
    minWidth: 0,
  },

  tagline: {
    color: "#D4AF37",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "4px",
  },

  title: {
    fontSize:
      "clamp(1.8rem, 3vw, 2.4rem)",
    margin: "6px 0 0",
    fontFamily:
      '"Playfair Display", serif',
    color: "#000000",
    fontWeight: "700",
    lineHeight: "1.2",
  },

  underline: {
    width: "40px",
    height: "2px",
    backgroundColor: "#D4AF37",
    marginTop: "8px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
    width: "100%",
  },

  productCard: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: "14px",
    overflow: "hidden",
    border:
      "1px solid rgba(75, 41, 84, 0.08)",
    boxShadow:
      "0 8px 24px rgba(75, 41, 84, 0.06)",
  },

  imageWrapper: {
    position: "relative",
    width: "100%",
    height: "320px",
    overflow: "hidden",
    backgroundColor: "#F3EBF5",
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  badgeContainer: {
    position: "absolute",
    top: "10px",
    left: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    zIndex: 2,
  },

  badgeBestseller: {
    backgroundColor: "#4B2954",
    color: "#FBF5FC",
    fontSize: "8.5px",
    padding: "4px 9px",
    borderRadius: "999px",
    fontWeight: "600",
    letterSpacing: "1px",
    boxShadow: "0 2px 6px rgba(75, 41, 84, 0.2)",
  },

  badgeDiscount: {
    backgroundColor: "#C22730",
    color: "#FFFFFF",
    fontSize: "8.5px",
    padding: "4px 9px",
    borderRadius: "999px",
    fontWeight: "600",
    letterSpacing: "1px",
    boxShadow: "0 2px 6px rgba(194, 39, 48, 0.2)",
  },

  desktopActionOverlay: {
    position: "absolute",
    bottom: "0",
    left: "0",
    right: "0",
    padding: "12px",
    boxSizing: "border-box",
    display: "flex",
    gap: "8px",
    background:
      "linear-gradient(to top, rgba(75, 41, 84, 0.85) 0%, rgba(75, 41, 84, 0.3) 75%, transparent 100%)",
    backdropFilter:
      "blur(3px)",
    zIndex: 2,
  },

  mobileActionRow: {
    display: "flex",
    gap: "8px",
    padding:
      "0 14px 14px 14px",
    boxSizing:
      "border-box",
    zIndex: 2,
  },

  quickView: {
    flex: 1,
    padding: "9px 8px",
    border: "1px solid rgba(75, 41, 84, 0.15)",
    borderRadius: "8px",
    backgroundColor:
      "rgba(255, 255, 255, 0.98)",
    color: "#4B2954",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "0.5px",
    textTransform:
      "uppercase",
    touchAction:
      "manipulation",
    boxShadow:
      "0 2px 6px rgba(0,0,0,0.06)",
  },

  addToCart: {
    flex: 1,
    padding: "9px 8px",
    border: "none",
    borderRadius: "8px",
    background:
      "linear-gradient(135deg, #e2bc53 0%, #c99e32 100%)",
    color: "#222222",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform:
      "uppercase",
    touchAction:
      "manipulation",
    boxShadow:
      "0 3px 10px rgba(212, 175, 55, 0.35)",
  },

  info: {
    padding:
      "16px 14px 8px 14px",
    minWidth: 0,
  },

  category: {
    color: "#8E7394",
    fontSize: "9px",
    margin:
      "0 0 4px",
    letterSpacing:
      "1.5px",
    textTransform:
      "uppercase",
    fontWeight: "500",
  },

  productName: {
    fontSize: "14px",
    margin:
      "0 0 8px",
    color: "#4B2954",
    fontFamily:
      '"Playfair Display", serif',
    fontWeight: "600",
    lineHeight: "1.3",
    whiteSpace:
      "nowrap",
    overflow:
      "hidden",
    textOverflow:
      "ellipsis",
  },

  priceRow: {
    display: "flex",
    alignItems:
      "center",
    gap: "8px",
    flexWrap:
      "wrap",
  },

  currentPrice: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#C22730",
  },

  oldPrice: {
    fontSize: "11px",
    color: "#9E8C9E",
    textDecoration:
      "line-through",
  },

  message: {
    width: "100%",
    textAlign:
      "center",
    padding:
      "60px 20px",
    color: "#4B2954",
    fontSize: "14px",
    fontWeight: "500",
  },
};

export default BestSellers;
